import { useEffect, useMemo, useRef, useState } from "react";
import { Building2, Download, FileUp, Home, Landmark, Plus, Users } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Breadcrumbs,
  EmptyState,
  Loading,
  SocietyPageHeader,
  WorkflowGuide,
  useTabParam,
} from "../../components/shared";
import { useSocietyFeatures } from "../../context/SocietyFeatureContext";

const PERMISSION = (context, module, action) =>
  Boolean(context?.permissions?.[module]?.includes(action));

export default function SocietyDirectory() {
  const [context, setContext] = useState(null);
  const [people, setPeople] = useState(null);
  const [units, setUnits] = useState(null);
  const [structures, setStructures] = useState(null);
  const [query, setQuery] = useState("");
  const [personOpen, setPersonOpen] = useState(false);
  const [unitOpen, setUnitOpen] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const openedUnitParam = useRef(null);

  const load = async (search = query) => {
    try {
      const [ctx, personData, unitData, structureData] = await Promise.all([
        api.get("/society-setup/context"),
        api.get("/society-directory/people", { params: { q: search } }),
        api.get("/society-directory/units", { params: { q: search } }),
        api.get("/society-setup/structure"),
      ]);
      setContext(ctx.data);
      setPeople(personData.data);
      setUnits(unitData.data);
      setStructures(structureData.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load Members & Units");
      setPeople({ items: [] });
      setUnits({ items: [] });
    }
  };

  useEffect(() => { load(); }, []);

  const can = (module, action) => PERMISSION(context, module, action);
  const { byKey } = useSocietyFeatures();
  const featureOn = (key) => Boolean(byKey?.(key)?.allowed);
  const availableTabs = useMemo(() => {
    const tabs = [];
    if (can("member_master", "view")) tabs.push("people");
    if (can("units", "view")) tabs.push("units");
    if (can("member_import", "import")) tabs.push("import");
    return tabs;
  }, [context]);
  const firstTab = availableTabs[0] || "people";
  const [tabParam, setDirTab] = useTabParam(firstTab);
  const dirTab = availableTabs.includes(tabParam) ? tabParam : firstTab;
  const requestedUnitId = searchParams.get("unit");

  useEffect(() => {
    if (!requestedUnitId || !units?.items || openedUnitParam.current === requestedUnitId) {
      return;
    }
    const requestedUnit = units.items.find((item) => item.unit_id === requestedUnitId);
    if (requestedUnit) {
      openedUnitParam.current = requestedUnitId;
      setUnitOpen(requestedUnit);
    }
  }, [requestedUnitId, units]);

  if (!people || !units) return <Loading />;

  return (
    <div data-testid="society-directory-page">
      <Breadcrumbs
        items={[
          { label: "Dashboard", to: "/app" },
          { label: "Society & Members" },
          { label: "Members & Units" },
        ]}
      />
      <SocietyPageHeader
        icon={Users}
        tone="amber"
        title="Members & Units"
        subtitle="People, ownership, residency, and tenancy remain separate historical records."
      >
        {can("member_master", "create") && (
          <Button data-testid="directory-add-person" onClick={() => setPersonOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Member
          </Button>
        )}
      </SocietyPageHeader>

      <section className="mb-6 flex flex-wrap gap-3">
        <Input
          data-testid="directory-search"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && load(event.currentTarget.value)}
          placeholder="Search person, mobile, email, or unit number"
          value={query}
        />
        <Button data-testid="directory-search-button" onClick={() => load()} variant="outline">
          Search
        </Button>
        {can("member_export", "export") && (
          <a data-testid="directory-export-units" href={`${API}/society-directory/export/units.csv`}>
            <Button variant="outline"><Download className="h-4 w-4" />Units CSV</Button>
          </a>
        )}
        {can("member_export", "export") && (
          <a data-testid="directory-export-people" href={`${API}/society-directory/export/people.csv`}>
            <Button variant="outline"><Download className="h-4 w-4" />Members CSV</Button>
          </a>
        )}
      </section>

      <Tabs onValueChange={setDirTab} value={dirTab}>
        <TabsList className="h-auto flex-wrap">
          {can("member_master", "view") && (
            <TabsTrigger data-testid="directory-people-tab" value="people">Members</TabsTrigger>
          )}
          {can("units", "view") && (
            <TabsTrigger data-testid="directory-units-tab" value="units">Units</TabsTrigger>
          )}
          {can("member_import", "import") && (
            <TabsTrigger data-testid="directory-import-tab" value="import">Bulk import</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="people">
          <PeopleTable items={people.items} />
        </TabsContent>
        <TabsContent value="units">
          {(can("structure", "create") || can("units", "create")) && (
            <StructureCreator
              structures={structures}
              people={people.items}
              canStructure={can("structure", "create")}
              canUnit={can("units", "create")}
              onSaved={load}
            />
          )}
          <UnitsTable items={units.items} onOpen={setUnitOpen} />
        </TabsContent>
        <TabsContent value="import">
          <ImportPanel onOpen={() => setImportOpen(true)} />
        </TabsContent>
      </Tabs>

      <WorkflowGuide
        steps={[
          {
            label: "Society Setup & Structure",
            to: "/app/setup?tab=structure",
            icon: Home,
            testid: "directory-guide-setup",
            show: can("profile", "view"),
          },
          {
            label: "Maintenance Finance",
            to: "/app/finance",
            icon: Landmark,
            testid: "directory-guide-finance",
            show: can("finance_billing", "view") && featureOn("sm03_billing"),
          },
        ]}
        title="Continue the Unit-to-Ledger workflow"
      />

      <PersonDialog onSaved={load} open={personOpen} onOpenChange={setPersonOpen} />
      <UnitDialog
        can={can}
        onOpenChange={setUnitOpen}
        onSaved={load}
        open={unitOpen}
        people={people.items}
        structures={structures}
      />
      <ImportDialog onOpenChange={setImportOpen} open={importOpen} onSaved={load} />
    </div>
  );
}

function PeopleTable({ items }) {
  if (!items.length) return <EmptyState icon={Users} title="No members in the Society Directory yet" />;
  return (
    <Table
      headers={["Member No.", "Person", "Type", "Mobile", "Email", "Status"]}
      testId="directory-people-table"
    >
      {items.map((person) => (
        <tr
          className="border-t"
          data-testid={`directory-person-${person.person_id}`}
          key={person.person_id}
        >
          <td className="p-3 font-mono text-xs">{person.member_number || "—"}</td>
          <td className="p-3 font-medium">{person.display_name}</td>
          <td className="p-3">{person.person_type}</td>
          <td className="p-3">{person.mobile || "—"}</td>
          <td className="p-3">{person.email || "—"}</td>
          <td className="p-3">{person.status}</td>
        </tr>
      ))}
    </Table>
  );
}

function StructureCreator({ structures, people = [], canStructure, canUnit, onSaved }) {
  const buildings = structures?.buildings || [];
  const [building, setBuilding] = useState({ name: "", building_code: "" });
  const [wing, setWing] = useState({ building_id: "", name: "" });
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);

  const post = async (path, payload, reset, message) => {
    try {
      await api.post(path, payload);
      toast.success(message);
      reset();
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save");
    }
  };

  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="directory-structure-creator">
      <div className="mb-3 flex items-center gap-2">
        <Building2 className="h-5 w-5 text-[#2563EB]" />
        <h3 className="font-display text-base font-bold text-[#0F2B63]">Add buildings, wings & units</h3>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {canStructure && (
          <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid="structure-building-form">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Building / Tower</div>
            <div className="space-y-2">
              <Input placeholder="Building name" value={building.name} data-testid="structure-building-name" onChange={(e) => setBuilding({ ...building, name: e.target.value })} />
              <Input placeholder="Code (optional)" value={building.building_code} data-testid="structure-building-code" onChange={(e) => setBuilding({ ...building, building_code: e.target.value })} />
              <Button size="sm" className="w-full" data-testid="structure-building-save"
                onClick={() => post("/society-setup/structure/buildings", building, () => setBuilding({ name: "", building_code: "" }), "Building added")}>
                <Plus className="h-4 w-4" /> Add building
              </Button>
            </div>
          </div>
        )}
        {canStructure && (
          <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid="structure-wing-form">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Wing</div>
            <div className="space-y-2">
              <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" value={wing.building_id} data-testid="structure-wing-building"
                onChange={(e) => setWing({ ...wing, building_id: e.target.value })}>
                <option value="">Select building</option>
                {buildings.map((b) => <option key={b.building_id} value={b.building_id}>{b.name}</option>)}
              </select>
              <Input placeholder="Wing name" value={wing.name} data-testid="structure-wing-name" onChange={(e) => setWing({ ...wing, name: e.target.value })} />
              <Button size="sm" className="w-full" data-testid="structure-wing-save" disabled={!wing.building_id}
                onClick={() => post("/society-setup/structure/wings", wing, () => setWing({ building_id: "", name: "" }), "Wing added")}>
                <Plus className="h-4 w-4" /> Add wing
              </Button>
            </div>
          </div>
        )}
        {canUnit && (
          <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid="structure-unit-form">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Unit / Flat</div>
            <p className="mb-3 text-xs text-slate-500">Flat, shop or office with floor, carpet / built-up areas (Sq.Ft.) and owners.</p>
            <Button size="sm" className="w-full" data-testid="structure-open-unit-dialog" onClick={() => setUnitDialogOpen(true)}>
              <Plus className="h-4 w-4" /> Add unit / flat
            </Button>
          </div>
        )}
      </div>
      <UnitCreateDialog
        open={unitDialogOpen}
        onOpenChange={setUnitDialogOpen}
        structures={structures}
        people={people}
        onSaved={onSaved}
      />
    </div>
  );
}

const UNIT_TYPE_OPTIONS = [
  { value: "FLAT", label: "Flat" },
  { value: "SHOP", label: "Shop" },
  { value: "OFFICE", label: "Office" },
  { value: "COMMERCIAL_UNIT", label: "Commercial unit" },
  { value: "GARAGE", label: "Garage" },
  { value: "GODOWN", label: "Godown" },
  { value: "OTHER", label: "Other" },
];

function UnitCreateDialog({ open, onOpenChange, structures, people = [], onSaved }) {
  const buildings = structures?.buildings || [];
  const wings = structures?.wings || [];
  const empty = {
    building_id: "",
    wing_id: "",
    unit_number: "",
    unit_type: "FLAT",
    floor: "",
    carpet_area: "",
    built_up_area: "",
    super_built_up_area: "",
    custom_chargeable_area: "",
    primary_owner_person_id: "",
    joint_owner_person_id: "",
  };
  const [form, setForm] = useState(empty);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const relevantWings = wings.filter((w) => w.building_id === form.building_id);

  const save = async () => {
    if (!form.unit_number.trim()) {
      toast.error("Unit number is required");
      return;
    }
    const payload = {
      building_id: form.building_id,
      wing_id: form.wing_id,
      unit_number: form.unit_number,
      unit_type: form.unit_type,
      floor: form.floor,
      carpet_area: form.carpet_area,
      built_up_area: form.built_up_area,
      super_built_up_area: form.super_built_up_area,
      custom_chargeable_area: form.custom_chargeable_area,
      primary_owner_person_id: form.primary_owner_person_id,
      joint_owner_person_ids: form.joint_owner_person_id ? [form.joint_owner_person_id] : [],
    };
    try {
      await api.post("/society-setup/units", payload);
      toast.success("Unit / flat created");
      setForm(empty);
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create unit");
    }
  };

  const areaField = (key, label) => (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-500">{label}</label>
      <Input
        data-testid={`unit-create-${key.replaceAll("_", "-")}`}
        min="0"
        onChange={(e) => set({ [key]: e.target.value })}
        placeholder="Sq.Ft."
        step="0.01"
        type="number"
        value={form[key]}
      />
    </div>
  );

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-2xl" data-testid="unit-create-dialog">
        <DialogHeader>
          <DialogTitle>Add Unit / Flat</DialogTitle>
          <DialogDescription>
            Select building & wing, set the unit type and areas, and optionally assign owners.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Building</label>
            <select
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              data-testid="unit-create-building"
              onChange={(e) => set({ building_id: e.target.value, wing_id: "" })}
              value={form.building_id}
            >
              <option value="">No building / standalone</option>
              {buildings.map((b) => <option key={b.building_id} value={b.building_id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Wing (optional)</label>
            <select
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              data-testid="unit-create-wing"
              onChange={(e) => set({ wing_id: e.target.value })}
              value={form.wing_id}
            >
              <option value="">No wing</option>
              {relevantWings.map((w) => <option key={w.wing_id} value={w.wing_id}>{w.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Unit number *</label>
            <Input
              data-testid="unit-create-number"
              onChange={(e) => set({ unit_number: e.target.value })}
              placeholder="e.g. A-101"
              value={form.unit_number}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Type of unit</label>
            <select
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              data-testid="unit-create-type"
              onChange={(e) => set({ unit_type: e.target.value })}
              value={form.unit_type}
            >
              {UNIT_TYPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Floor</label>
            <Input
              data-testid="unit-create-floor"
              onChange={(e) => set({ floor: e.target.value })}
              placeholder="e.g. 1"
              value={form.floor}
            />
          </div>
          {areaField("carpet_area", "Carpet Area (Sq.Ft.)")}
          {areaField("built_up_area", "Built-Up Area (Sq.Ft.)")}
          {areaField("super_built_up_area", "Super Built-Up Area (Sq.Ft.)")}
          {areaField("custom_chargeable_area", "Custom Chargeable Area (Sq.Ft.)")}
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Primary owner (optional)</label>
            <select
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              data-testid="unit-create-primary-owner"
              onChange={(e) => set({ primary_owner_person_id: e.target.value })}
              value={form.primary_owner_person_id}
            >
              <option value="">Not assigned</option>
              {people.filter((p) => p.person_id !== form.joint_owner_person_id).map((p) => (
                <option key={p.person_id} value={p.person_id}>{p.display_name}{p.member_number ? ` (${p.member_number})` : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Joint owner (optional)</label>
            <select
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
              data-testid="unit-create-joint-owner"
              onChange={(e) => set({ joint_owner_person_id: e.target.value })}
              value={form.joint_owner_person_id}
            >
              <option value="">Not assigned</option>
              {people.filter((p) => p.person_id !== form.primary_owner_person_id).map((p) => (
                <option key={p.person_id} value={p.person_id}>{p.display_name}{p.member_number ? ` (${p.member_number})` : ""}</option>
              ))}
            </select>
          </div>
        </div>
        <Button data-testid="unit-create-save" onClick={save}>Save unit</Button>
      </DialogContent>
    </Dialog>
  );
}

function UnitsTable({ items, onOpen }) {
  if (!items.length) return <EmptyState icon={Home} title="No units match this directory filter" />;
  return <Table headers={["Unit", "Floor", "Type", "Primary owner", "Occupancy", "Action"]} testId="directory-units-table">{items.map((unit) => <tr className="border-t" data-testid={`directory-unit-${unit.unit_id}`} key={unit.unit_id}><td className="p-3 font-medium">{unit.unit_number}</td><td className="p-3">{unit.floor || "—"}</td><td className="p-3">{unit.unit_type}</td><td className="p-3">{unit.primary_owner?.display_name || "—"}</td><td className="p-3">{unit.current_occupancy_status || "VACANT"}</td><td className="p-3"><Button data-testid={`directory-open-unit-${unit.unit_id}`} onClick={() => onOpen(unit)} size="sm" variant="outline">Unit 360</Button></td></tr>)}</Table>;
}

function ImportPanel({ onOpen }) {
  return <section className="mt-5 border bg-card p-5" data-testid="directory-import-panel"><h2 className="font-display font-bold">Unit + Member onboarding</h2><p className="mt-1 text-sm text-muted-foreground">Upload CSV or XLSX, validate before writing, review the preview, then confirm a single idempotent import.</p><div className="mt-5 flex flex-wrap gap-3"><a data-testid="directory-template-xlsx" href={`${API}/society-directory/import/template?format=xlsx`}><Button variant="outline"><Download className="h-4 w-4" />Excel template</Button></a><a data-testid="directory-template-csv" href={`${API}/society-directory/import/template?format=csv`}><Button variant="outline"><Download className="h-4 w-4" />CSV template</Button></a><Button data-testid="directory-open-import" onClick={onOpen}><FileUp className="h-4 w-4" />Validate import</Button></div></section>;
}

function PersonDialog({ onOpenChange, onSaved, open }) {
  const empty = {
    email: "",
    first_name: "",
    last_name: "",
    member_number: "",
    membership_date: "",
    mobile: "",
    alternate_mobile: "",
    alternate_email: "",
    correspondence_address: "",
    permanent_address: "",
    city: "",
    state: "",
    pincode: "",
    person_type: "INDIVIDUAL",
    profession: "",
    share_certificate_number: "",
  };
  const [form, setForm] = useState(empty);
  const save = async () => {
    try {
      await api.post("/society-directory/people", form);
      toast.success("Member created with a permanent Society Member Number");
      setForm(empty);
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create person");
    }
  };
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-2xl" data-testid="person-dialog">
        <DialogHeader>
          <DialogTitle>Add Member</DialogTitle>
          <DialogDescription>
            Leave Member Number blank to auto-generate the next number (#001, #002, …). Entered
            numbers are stored in uppercase.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <select
            data-testid="person-type"
            onChange={(event) => setForm({ ...form, person_type: event.target.value })}
            value={form.person_type}
          >
            <option value="INDIVIDUAL">Individual</option>
            <option value="ORGANIZATION">Organization</option>
          </select>
          <Input
            data-testid="person-member-number"
            onChange={(event) => setForm({ ...form, member_number: event.target.value })}
            placeholder="Member Number (optional)"
            value={form.member_number}
          />
          {form.person_type === "ORGANIZATION" ? (
            <Input
              data-testid="person-organization-name"
              onChange={(event) => setForm({ ...form, organization_name: event.target.value })}
              placeholder="Organization name *"
              value={form.organization_name || ""}
            />
          ) : (
            <>
              <Input
                data-testid="person-first-name"
                onChange={(event) => setForm({ ...form, first_name: event.target.value })}
                placeholder="First name *"
                value={form.first_name}
              />
              <Input
                data-testid="person-last-name"
                onChange={(event) => setForm({ ...form, last_name: event.target.value })}
                placeholder="Last name"
                value={form.last_name}
              />
            </>
          )}
          <Input
            data-testid="person-mobile"
            onChange={(event) => setForm({ ...form, mobile: event.target.value })}
            placeholder="Mobile *"
            value={form.mobile}
          />
          <Input
            data-testid="person-email"
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            placeholder="Email"
            value={form.email}
          />
          <Input
            data-testid="person-alternate-mobile"
            onChange={(event) => setForm({ ...form, alternate_mobile: event.target.value })}
            placeholder="Alternate mobile"
            value={form.alternate_mobile}
          />
          <Input
            data-testid="person-alternate-email"
            onChange={(event) => setForm({ ...form, alternate_email: event.target.value })}
            placeholder="Alternate email"
            value={form.alternate_email}
          />
          <Input
            data-testid="person-membership-date"
            onChange={(event) => setForm({ ...form, membership_date: event.target.value })}
            type="date"
            value={form.membership_date}
          />
          <Input
            data-testid="person-profession"
            onChange={(event) => setForm({ ...form, profession: event.target.value })}
            placeholder="Profession / occupation"
            value={form.profession}
          />
          <Input
            data-testid="person-share-certificate"
            onChange={(event) => setForm({ ...form, share_certificate_number: event.target.value })}
            placeholder="Share certificate number"
            value={form.share_certificate_number}
          />
          <Input
            data-testid="person-correspondence-address"
            onChange={(event) => setForm({ ...form, correspondence_address: event.target.value })}
            placeholder="Correspondence address"
            value={form.correspondence_address}
          />
          <Input
            data-testid="person-permanent-address"
            onChange={(event) => setForm({ ...form, permanent_address: event.target.value })}
            placeholder="Permanent address"
            value={form.permanent_address}
          />
          <Input
            data-testid="person-city"
            onChange={(event) => setForm({ ...form, city: event.target.value })}
            placeholder="City"
            value={form.city}
          />
          <Input
            data-testid="person-state"
            onChange={(event) => setForm({ ...form, state: event.target.value })}
            placeholder="State"
            value={form.state}
          />
          <Input
            data-testid="person-pincode"
            onChange={(event) => setForm({ ...form, pincode: event.target.value })}
            placeholder="PIN code"
            value={form.pincode}
          />
        </div>
        <Button data-testid="person-save" onClick={save}>Save Member</Button>
      </DialogContent>
    </Dialog>
  );
}

function UnitDialog({ can, onOpenChange, onSaved, open, people }) {
  const [detail, setDetail] = useState(null);
  const [owner, setOwner] = useState({ person_id: "", ownership_role: "PRIMARY_OWNER" });
  const [occupant, setOccupant] = useState({ person_id: "", occupancy_role: "TENANT", is_primary: true });
  const [transferForm, setTransferForm] = useState({
    effective_date: new Date().toISOString().slice(0, 10),
    person_id: "",
    transfer_reason: "SALE",
    transfer_reference: "",
  });

  const reload = async () => {
    const response = await api.get(`/society-directory/units/${open.unit_id}`);
    setDetail(response.data);
    onSaved();
  };

  useEffect(() => {
    if (!open?.unit_id) {
      setDetail(null);
      return;
    }
    api.get(`/society-directory/units/${open.unit_id}`).then((response) => setDetail(response.data));
  }, [open?.unit_id]);

  const post = async (path, payload) => {
    try {
      await api.post(path, payload);
      toast.success("Unit relationship saved");
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save relationship");
    }
  };

  const transfer = async () => {
    if (!transferForm.person_id) {
      toast.error("Choose the new primary owner");
      return;
    }
    try {
      await api.post(`/society-directory/units/${open.unit_id}/ownership-transfer`, {
        effective_date: transferForm.effective_date,
        owners: [{ person_id: transferForm.person_id, ownership_role: "PRIMARY_OWNER" }],
        transfer_reason: transferForm.transfer_reason,
        transfer_reference: transferForm.transfer_reference,
      });
      toast.success("Ownership history updated; the unit ledger was not changed");
      setTransferForm({
        effective_date: new Date().toISOString().slice(0, 10),
        person_id: "",
        transfer_reason: "SALE",
        transfer_reference: "",
      });
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not transfer ownership");
    }
  };

  if (!open) return null;
  return (
    <Dialog onOpenChange={() => onOpenChange(null)} open={Boolean(open)}>
      <DialogContent className="max-w-4xl" data-testid="unit-360-dialog">
        <DialogHeader>
          <DialogTitle>Unit 360 · {open.unit_number}</DialogTitle>
          <DialogDescription>
            Ownership and occupancy are tracked independently with full history.
          </DialogDescription>
        </DialogHeader>
        {!detail ? <Loading /> : <div className="space-y-5">
          <UnitSnapshot detail={detail} />
          {detail.warnings?.length > 0 && (
            <section className="border border-amber-300 bg-amber-50 p-4" data-testid="unit-360-warnings">
              {detail.warnings.map((warning) => <p className="text-sm" key={warning}>{warning}</p>)}
            </section>
          )}
          <section className="border bg-muted/20 p-4" data-testid="unit-360-addressees">
            <h3 className="font-medium">Financial document addressee</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {detail.owner_addressees?.display_name || "No eligible active owner"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {String(detail.owner_addressees?.mode || "PRIMARY_OWNER_ONLY").replaceAll("_", " ")}
            </p>
          </section>
          <RelationshipList title="Current owners" rows={detail.current_owners} />
          <RelationshipList title="Current occupants" rows={detail.current_occupants} />
          {can("ownership", "transfer") && (
            <section className="border border-amber-300 bg-amber-50 p-4">
              <h3 className="font-medium">Transfer ownership</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                This closes current ownership only. Occupants and financial records remain unchanged.
              </p>
              <div className="mt-3 flex flex-wrap gap-3">
                <Input
                  data-testid="unit-transfer-effective-date"
                  onChange={(event) => setTransferForm({
                    ...transferForm,
                    effective_date: event.target.value,
                  })}
                  type="date"
                  value={transferForm.effective_date}
                />
                <select
                  data-testid="unit-transfer-owner"
                  onChange={(event) => setTransferForm({
                    ...transferForm,
                    person_id: event.target.value,
                  })}
                  value={transferForm.person_id}
                >
                  <option value="">Choose new primary owner</option>
                  {people.map((person) => (
                    <option key={person.person_id} value={person.person_id}>
                      {person.display_name}
                    </option>
                  ))}
                </select>
                <select
                  data-testid="unit-transfer-reason"
                  onChange={(event) => setTransferForm({
                    ...transferForm,
                    transfer_reason: event.target.value,
                  })}
                  value={transferForm.transfer_reason}
                >
                  <option value="SALE">Sale</option>
                  <option value="GIFT">Gift</option>
                  <option value="INHERITANCE">Inheritance</option>
                  <option value="OTHER">Other</option>
                </select>
                <Input
                  data-testid="unit-transfer-reference"
                  onChange={(event) => setTransferForm({
                    ...transferForm,
                    transfer_reference: event.target.value,
                  })}
                  placeholder="Deed / transfer reference (optional)"
                  value={transferForm.transfer_reference}
                />
                <Button data-testid="unit-transfer-submit" onClick={transfer} size="sm">
                  Transfer ownership
                </Button>
              </div>
            </section>
          )}
          {can("ownership", "create") && (
            <RelationForm
              label="Assign owner"
              onSave={() => post(`/society-directory/units/${open.unit_id}/ownerships`, owner)}
              people={people}
              setValue={setOwner}
              value={owner}
            >
              <select
                data-testid="unit-owner-role"
                onChange={(event) => setOwner({ ...owner, ownership_role: event.target.value })}
                value={owner.ownership_role}
              >
                <option value="PRIMARY_OWNER">Primary owner</option>
                <option value="JOINT_OWNER">Joint owner</option>
                <option value="ASSOCIATE_MEMBER">Associate member</option>
              </select>
              <Input
                data-testid="unit-owner-display-order"
                min="1"
                onChange={(event) => setOwner({ ...owner, display_order: event.target.value })}
                placeholder="Display order"
                type="number"
                value={owner.display_order || ""}
              />
            </RelationForm>
          )}
          {can("occupancy", "create") && (
            <RelationForm
              label="Add occupant"
              onSave={() => post(`/society-directory/units/${open.unit_id}/occupancies`, occupant)}
              people={people}
              setValue={setOccupant}
              value={occupant}
            >
              <select
                data-testid="unit-occupant-role"
                onChange={(event) => setOccupant({ ...occupant, occupancy_role: event.target.value })}
                value={occupant.occupancy_role}
              >
                <option value="TENANT">Tenant</option>
                <option value="OWNER_RESIDENT">Owner resident</option>
                <option value="FAMILY_MEMBER">Family member</option>
                <option value="AUTHORIZED_OCCUPANT">Authorized occupant</option>
              </select>
            </RelationForm>
          )}
          <RelationshipList title="Ownership history" rows={detail.ownership_history} />
          <RelationshipList title="Occupancy history" rows={detail.occupancy_history} />
          <UnitReferences detail={detail} />
        </div>}
      </DialogContent>
    </Dialog>
  );
}

function UnitSnapshot({ detail }) {
  const unit = detail.unit || {};
  const areas = [
    ["Carpet", unit.carpet_area],
    ["Built-up", unit.built_up_area],
    ["Super built-up", unit.super_built_up_area],
    ["Custom chargeable", unit.custom_chargeable_area],
  ].filter(([, value]) => value);

  return (
    <section className="grid gap-3 border bg-card p-4 sm:grid-cols-2" data-testid="unit-360-summary">
      <div>
        <p className="text-xs text-muted-foreground">Unit</p>
        <p className="font-medium">{unit.unit_number} · {unit.unit_type || "Unit"}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Occupancy</p>
        <p className="font-medium">{unit.current_occupancy_status || unit.occupancy_status || "VACANT"}</p>
      </div>
      <div className="sm:col-span-2">
        <p className="text-xs text-muted-foreground">Recorded areas</p>
        <p className="font-medium">{areas.map(([label, value]) => `${label}: ${value} Sq.Ft.`).join(" · ") || "None recorded"}</p>
      </div>
    </section>
  );
}

function UnitReferences({ detail }) {
  const allocations = detail.parking?.active_allocations || [];
  const vehicles = detail.parking?.vehicles || [];
  const documents = detail.documents || [];

  return (
    <section className="grid gap-4 md:grid-cols-3" data-testid="unit-360-references">
      <ReferenceList
        items={documents.map((item) => item.title || item.filename)}
        testId="unit-360-documents"
        title="Documents"
      />
      <ReferenceList
        items={allocations.map((item) => item.slot_snapshot?.slot_number || item.allocation_id)}
        testId="unit-360-parking-allocations"
        title="Active parking"
      />
      <ReferenceList
        items={vehicles.map((item) => item.registration_number || item.vehicle_id)}
        testId="unit-360-vehicles"
        title="Vehicles"
      />
    </section>
  );
}

function ReferenceList({ items, testId, title }) {
  return (
    <section className="border bg-card p-4" data-testid={testId}>
      <h3 className="font-medium">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{items.join(", ") || "None recorded."}</p>
    </section>
  );
}

function RelationForm({ children, label, onSave, people, setValue, value }) { return <section className="border bg-muted/30 p-4"><h3 className="font-medium">{label}</h3><div className="mt-3 flex flex-wrap gap-3"><select data-testid={`${label.toLowerCase().replaceAll(" ", "-")}-person`} onChange={(event) => setValue({ ...value, person_id: event.target.value })} value={value.person_id}><option value="">Choose person</option>{people.map((person) => <option key={person.person_id} value={person.person_id}>{person.display_name}</option>)}</select>{children}<Button data-testid={`${label.toLowerCase().replaceAll(" ", "-")}-save`} onClick={onSave} size="sm">Save</Button></div></section>; }

function RelationshipList({ rows, title }) {
  return (
    <section>
      <h3 className="font-display font-bold">{title}</h3>
      <div className="mt-2 space-y-2">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">None recorded.</p>
        ) : (
          rows.map((row) => (
            <div className="border bg-card p-3 text-sm" key={row.ownership_id || row.occupancy_id}>
              <span className="font-medium">{row.person?.display_name || row.person_id}</span>
              {" · "}
              {row.ownership_role || row.occupancy_role}
              {" · "}
              {row.start_date}
              {row.end_date ? ` to ${row.end_date}` : ""}
              {row.transfer_reason ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Transfer: {row.transfer_reason}
                  {row.transfer_reference ? ` · Ref: ${row.transfer_reference}` : ""}
                </p>
              ) : null}
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function ImportDialog({ onOpenChange, onSaved, open }) { const [job, setJob] = useState(null); const upload = async (event) => { const file = event.target.files?.[0]; if (!file) return; const form = new FormData(); form.append("file", file); try { const response = await api.post("/society-directory/import/validate", form); setJob(response.data); } catch (error) { toast.error(error.response?.data?.detail || "Could not validate import"); } event.target.value = ""; }; const confirm = async () => { try { await api.post(`/society-directory/import/${job.import_job_id}/confirm`); toast.success("Import completed"); onOpenChange(false); onSaved(); } catch (error) { toast.error(error.response?.data?.detail || "Could not complete import"); } }; return <Dialog onOpenChange={onOpenChange} open={open}><DialogContent><DialogHeader><DialogTitle>Validate import</DialogTitle></DialogHeader><input accept=".csv,.xlsx" data-testid="directory-import-file" onChange={upload} type="file" />{job && <div className="space-y-3"><div data-testid="directory-import-summary">{job.summary.valid_rows} valid · {job.summary.errors} errors · {job.summary.warnings} warnings</div>{job.errors?.length > 0 && <ul className="text-sm text-destructive">{job.errors.map((item) => <li key={item.row}>Row {item.row}: {item.errors.join(", ")}</li>)}</ul>}{job.summary.errors === 0 && <Button data-testid="directory-import-confirm" onClick={confirm}>Confirm import</Button>}</div>}</DialogContent></Dialog>; }

function Table({ children, headers, testId }) { return <div className="mt-5 overflow-x-auto border bg-card" data-testid={testId}><table className="min-w-[760px] w-full text-sm"><thead className="bg-muted/60"><tr>{headers.map((header) => <th className="p-3 text-left" key={header}>{header}</th>)}</tr></thead><tbody>{children}</tbody></table></div>; }